import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const PermissionsContext = createContext(null);

// Permission key to navigation mapping
export const PERMISSION_NAV_MAP = {
  // Main Navigation
  dashboard: ['dashboard.view'],
  attendance: ['attendance.view'],
  enrollment: ['enrollment.view'],
  children: ['children.view'],
  rooms: ['rooms.view'],
  programs: ['programs.view'],
  compliance: ['compliance.view'],
  centers: ['centers.view', 'centers.manage'],
  sessions: ['settings.view'],
  parents: ['parents.view'],
  
  // Daily Logs
  'daily-logs': ['daily_logs.view', 'daily_logs.create'],
  'daily-logs-naps': ['daily_logs.naps'],
  'daily-logs-mood': ['daily_logs.mood'],
  
  // Curriculum
  'curriculum-lessons': ['curriculum.view'],
  'curriculum-ai-planner': ['curriculum.ai_planner'],
  'curriculum-observations': ['curriculum.observations'],
  'curriculum-work-samples': ['curriculum.work_samples'],
  'curriculum-settings': ['curriculum.settings'],
  
  // CRM
  'crm': ['crm.view'],
  'crm-leads': ['crm.leads'],
  'crm-pipeline': ['crm.view', 'crm.leads'],
  'crm-tours': ['crm.tours'],
  'crm-followups': ['crm.followups'],
  
  // Analytics
  analytics: ['analytics.view'],
  'analytics-export': ['analytics.export'],
  
  // Staff Management
  'staff-management': ['staff.view', 'staff.manage'],
  'staff-attendance': ['staff.attendance'],
  'staff-directory': ['staff.view'],
  'staff-training': ['staff.training'],
  'staff-leave': ['staff.leave'],
  'staff-performance': ['staff.performance'],
  
  // Communication
  'email-compose': ['communication.compose'],
  'email-sent': ['communication.view'],
  'email-scheduled': ['communication.view'],
  'email-drafts': ['communication.drafts'],
  
  // Billing
  'billing-invoices': ['billing.view'],
  'billing-payments': ['billing.view', 'billing.record_payment'],
  'billing-settings': ['billing.settings'],
  
  // Settings (Admin Only)
  'settings-permissions': ['settings.permissions'],
  'settings-activity-types': ['settings.edit'],
  'settings-email-templates': ['settings.email_templates'],
  'settings-email': ['settings.edit'],
  'settings-parent-invitations': ['settings.edit'],
};

// Navigation sections that require specific roles (not just permissions)
export const ADMIN_ONLY_SECTIONS = [
  'settings',
  'billing-settings',
  'settings-permissions',
  'settings-email-templates',
  'settings-parent-invitations',
];

export function PermissionsProvider({ children, user }) {
  const [permissions, setPermissions] = useState([]);
  const [assignedCenters, setAssignedCenters] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Admin roles automatically have all permissions
    if (user.role === 'super_admin' || user.role === 'tenant_admin') {
      setIsAdmin(true);
      setPermissions(Object.values(PERMISSION_NAV_MAP).flat());
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/permissions/my-permissions');
      const data = response.data;
      
      setPermissions(data.permissions || []);
      setAssignedCenters(data.assigned_centers || []);
      setIsAdmin(data.is_admin || false);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError(err.message);
      // On error, provide minimal permissions for safety
      setPermissions(['dashboard.view']);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has at least one of the required permissions
  const hasPermission = useCallback((requiredPermissions) => {
    if (isAdmin) return true;
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    
    return requiredPermissions.some(perm => permissions.includes(perm));
  }, [permissions, isAdmin]);

  // Check if user can access a specific navigation item
  const canAccessNav = useCallback((navKey) => {
    if (isAdmin) return true;
    
    // Check if this is an admin-only section
    if (ADMIN_ONLY_SECTIONS.includes(navKey)) {
      return false;
    }
    
    const requiredPerms = PERMISSION_NAV_MAP[navKey];
    if (!requiredPerms) return true; // If not mapped, allow access
    
    return hasPermission(requiredPerms);
  }, [hasPermission, isAdmin]);

  // Check if user has access to a specific center
  const hasAccessToCenter = useCallback((centerId) => {
    if (isAdmin) return true;
    if (!centerId) return true;
    
    return assignedCenters.some(c => c.id === centerId);
  }, [assignedCenters, isAdmin]);

  // Get the user's default center (first assigned center)
  const getDefaultCenter = useCallback(() => {
    if (assignedCenters.length > 0) {
      return assignedCenters[0];
    }
    return null;
  }, [assignedCenters]);

  const value = {
    permissions,
    assignedCenters,
    isAdmin,
    loading,
    error,
    hasPermission,
    canAccessNav,
    hasAccessToCenter,
    getDefaultCenter,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
